'use client';

import type { KeyboardEvent } from 'react';
import type { UserQuestDto } from '@/queries/userQuest/userQuest.schema';
import { Icon } from '@/components/entities/Sprite';
import { cn } from '@/utils/cn';
import { formatCentsToCurrencyNumber, formatCompactNumber } from '@/utils/currency.utils';
import { calculateProgressPercent } from '@/utils/progress.utils';
import { GmProgressBar } from '../GmProgressBar';

interface Props extends UserQuestDto {
  onAction?: VoidFunction;
}

const MIN_FILL_VIEW_PROGRESS_BAR = 5;

export const GmMissionCard = ({ onAction, ...mission }: Props) => {
  const isSumTotalProgress = mission.quest.progress.type === 'sum';
  const done = (
    isSumTotalProgress
      ? formatCentsToCurrencyNumber(mission.progress)
      : mission.progress ?? 0
  );
  const total = (isSumTotalProgress ? formatCentsToCurrencyNumber : (val: number) => val)(mission.quest.progress.target ?? 0);
  const percentageProgress = calculateProgressPercent(done, total);
  const title = mission.quest.name;
  const xp = mission.quest.xpAmount ?? 0;

  const handleMissionKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onAction?.();
    }
  };

  const missionsProgress = Math.min(percentageProgress, 100);
  const missionCompleted = !!mission.completedAt;
  const missionPercentProgress = Math.max(missionsProgress, done ? MIN_FILL_VIEW_PROGRESS_BAR : 0);

  return (
    <div
      {
        ...(
          onAction
            ? {
                role: 'button',
                tabIndex: 0,
                onClick: onAction,
                onKeyDown: handleMissionKeyDown,
              }
            : {}
        )
      }
      className={cn(
        // TODO: need confirmation with design, cause h-fit make weird view
        'rounded-2xl glass-stroke-border box-border shadow-card gm-mission-gradient w-full',
        'lg:gap-4 gap-0 py-4 min-h-22 lg:py-4 flex justify-between items-center pl-4 pr-6 lg:pr-4',
        onAction ? 'lg:pr-4 pr-1 cursor-pointer' : 'lg:pr-10 pr-6',
        missionCompleted && 'opacity-50',
      )}
    >
      <div className="flex flex-col gap-3 pr-2 grow">
        <p className="text-sm text-start font-normal leading-5 text-light">
          {title}
        </p>

        <div className="relative">
          <GmProgressBar
            className="**:data-[slot=progressBarTrack]:z-10"
            showLightEffect={false}
            percent={missionPercentProgress}
          />

          <p className="text-c300 font-semibold leading-[0.813rem] absolute lg:mr-px right-2 top-0 text-[0.688rem]">
            {`${done}/${total}`}
          </p>
        </div>
      </div>

      <div className="flex justify-center items-center">

        <div className="flex relative gm-mission-xp-wrapper-border gm-mission-xp-shadow font-semibold flex-col justify-center items-center rounded-full min-w-12.5 min-h-12.5 bg-main-800/40 px-2">
          <p className="text-lg leading-4.5 tracking-tighter gm-mission-xp-bg">
            {formatCompactNumber(xp, { minUnit: 'thousand' })}
          </p>
          <p className="text-sm leading-3 gm-mission-xp-bg">
            XP
          </p>
        </div>
        {
          !!onAction && (
            <Icon name="arrow-pagination" className="size-5 lg:size-6 text-light rotate-180" />
          )
        }
      </div>
    </div>
  );
};
